import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, PopoverMenu as Popover, Text } from '@factorialco/deprecated-design-system'
import { SignedInvoiceExpensableFragment, SignedInvoiceFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { usePortal } from 'modules/core/lib/portalContext'

interface NavigatorProps {
  invoice: SignedInvoiceFragment
  expensable?: SignedInvoiceExpensableFragment
  invoiceIdsList: { id: number }[]
}

const SignedDocumentNavigator = ({ invoice, expensable, invoiceIdsList }: NavigatorProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const currentIndex = invoiceIdsList.findIndex(({ id }) => id === invoice.id)
  const expensableUrl = expensable
    ? navigation.expenses.list.expenses.expenseId.fullPath({
        expense_id: expensable.id,
      })
    : null

  const onClickBack = () => {
    const previousInvoice = invoiceIdsList[currentIndex - 1]
    if (previousInvoice) navigateInvoice(previousInvoice.id)
  }

  const onClickNext = () => {
    const nextInvoice = invoiceIdsList[currentIndex + 1]
    if (nextInvoice) navigateInvoice(nextInvoice.id)
  }

  const navigateInvoice = (target: number) => {
    const invoiceUrl = navigation.settings.expenses.signedInvoices.invoiceId.fullPath({
      invoiceId: target,
    })

    navigate(invoiceUrl)
  }

  const { closePortal } = usePortal()

  const onClose = (event: React.SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
    closePortal()
  }

  return (
    <Box
      flexDirection="row"
      gap="s8"
      justifyContent="flexStart"
      alignItems="center"
      paddingX="s16"
      paddingY="s8"
      height="s64"
      border={{ bottom: { width: 's1', style: 'solid', color: 'grey400' } }}
    >
      <Button onClick={onClose} type="primary" iconName="cross" />
      <Box
        flexDirection="row"
        gap="s16"
        justifyContent="center"
        alignItems="center"
        width="full"
        paddingX="s16"
      >
        <Button
          disabled={currentIndex <= 0}
          onClick={onClickBack}
          type="primary"
          iconName="arrow-left"
        />

        <Box flexDirection="row" gap="s6" justifyContent="flexStart" alignItems="center">
          <Text.Normal weight="medium" size="300">
            {i18n.t('expenses.settings.signed_invoices.detail.navigator.title')}
          </Text.Normal>
        </Box>

        <Button
          disabled={currentIndex >= invoiceIdsList.length - 1}
          onClick={onClickNext}
          type="primary"
          iconName="arrow-right"
        />
      </Box>
      {invoice.parentExpense?.status !== 'draft' && expensableUrl && (
        <Box
          flexDirection="row"
          gap="s6"
          paddingRight="s24"
          justifyContent="flexEnd"
          alignItems="center"
        >
          <Popover type="list">
            <Popover.Trigger>
              <Button.Decorative type="secondary" iconName="ellipsis" />
            </Popover.Trigger>
            <Popover.Content side="bottom" align="end">
              <Popover.TouchableOption
                onClick={() => navigate(expensableUrl)}
                title={i18n.t('expenses.settings.signed_invoices.detail.navigator.view_expense')}
              />
            </Popover.Content>
          </Popover>
        </Box>
      )}
    </Box>
  )
}

export default SignedDocumentNavigator
